/**
 * Single-row header — overflow safety net.
 *
 * Which density level each width gets is decided in CSS (see sections/header.liquid), so
 * the row is already correct at the first paint and does not move afterwards. This file
 * only handles the cases CSS cannot know about: a market whose menu labels are longer than
 * the English ones, a font that renders wider than measured, or a browser zoom level that
 * lands between the breakpoints.
 *
 * It can only ever tighten. Loosening a row that already fits is what makes a header jump
 * under the reader's eyes on load, and it buys nothing.
 *
 * It is loaded synchronously right after the header markup rather than deferred, so any
 * correction happens before the first paint too. header.js calls back in on resize.
 */
(function () {
  var MAX_LEVEL = 7;
  var SELECTOR = '.header__top--logo-left-menu-inline';
  var STACKED = 'header__top--menu-stacked';

  /**
   * Intrinsic widths only — item content widths, the search field's resolved min-width —
   * never a current column width, which would change with the level being tested.
   */
  function rowFits(headerTop, menuList) {
    var styles = getComputedStyle(headerTop);
    var available =
      headerTop.clientWidth - (parseFloat(styles.paddingLeft) || 0) - (parseFloat(styles.paddingRight) || 0);
    var columnGap = parseFloat(styles.columnGap) || 0;

    var menuWidth = Array.prototype.reduce.call(
      menuList.children,
      function (total, item) {
        return total + item.offsetWidth;
      },
      0
    );

    var logo = headerTop.querySelector('.header__logo');
    var icons = headerTop.querySelector('.header__icons--right');
    var searchForm = headerTop.querySelector('.header__search .search__form');
    var searchWidth = searchForm ? parseFloat(getComputedStyle(searchForm).minWidth) || searchForm.offsetWidth : 0;

    var required =
      (logo ? logo.offsetWidth : 0) + menuWidth + searchWidth + (icons ? icons.offsetWidth : 0) + columnGap * 3;

    // Small cushion so the menu never ends up flush against the search field.
    return required + 8 <= available;
  }

  function currentLevel(headerTop) {
    return parseInt(getComputedStyle(headerTop).getPropertyValue('--fit-level'), 10) || 0;
  }

  function fit(headerTop, options) {
    if (!headerTop) return;
    options = options || {};

    var navigation = headerTop.querySelector('.header__navigation--inline');
    var menuList = navigation && navigation.querySelector('.header__menu > ul');

    // Below the desktop breakpoint the menu lives in the drawer, so there is nothing to fit.
    if (!menuList || getComputedStyle(navigation).display === 'none') {
      headerTop.removeAttribute('data-header-fit');
      headerTop.classList.remove(STACKED);
      headerTop.fitRowWidth = null;
      return;
    }

    // The verdict only changes when the row changes width, or when the font finally lands.
    var rowWidth = headerTop.clientWidth;
    if (!options.force && rowWidth === headerTop.fitRowWidth) return;
    headerTop.fitRowWidth = rowWidth;

    // Start from what CSS chose for this width. Reading layout in between does not paint,
    // so dropping any previous override here is not visible.
    headerTop.removeAttribute('data-header-fit');
    headerTop.classList.remove(STACKED);

    if (rowFits(headerTop, menuList)) {
      watchFont(headerTop, navigation);
      return;
    }

    for (var level = currentLevel(headerTop) + 1; level <= MAX_LEVEL; level++) {
      headerTop.setAttribute('data-header-fit', String(level));
      if (rowFits(headerTop, menuList)) {
        watchFont(headerTop, navigation);
        return;
      }
    }

    // Nothing fits: full size, with the menu on its own line.
    headerTop.removeAttribute('data-header-fit');
    headerTop.classList.add(STACKED);
    watchFont(headerTop, navigation);
  }

  /**
   * Re-check once the navigation font is actually usable, since the labels are measured in
   * the fallback face until then. Waiting on document.fonts.ready would mean waiting for
   * every font on the page — seconds on a cold load.
   */
  function watchFont(headerTop, navigation) {
    if (headerTop.fitFontWatched || !document.fonts || !document.fonts.load) return;
    headerTop.fitFontWatched = true;

    var styles = getComputedStyle(navigation);
    var font = styles.fontWeight + ' ' + styles.fontSize + ' ' + styles.fontFamily;

    try {
      document.fonts
        .load(font)
        .then(function () {
          fit(headerTop, { force: true });
        })
        .catch(function () {});
    } catch (error) {
      /* Malformed font shorthand — leave the CSS decision alone. */
    }
  }

  window.FoxHeaderFit = { fit: fit, selector: SELECTOR, maxLevel: MAX_LEVEL };

  // Pre-paint pass — see the note above.
  fit(document.querySelector(SELECTOR), { force: true });
})();
